import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useOrientationStore } from '@/stores/orientation.store'
import { orientationAPI } from '@/lib/orientationApi'
import toast from 'react-hot-toast'

const FEEDBACK_TYPES = [
  { value: 'ADMITTED', label: 'Patient admis', icon: '✓' },
  { value: 'REFUSED', label: 'Refuse par la structure', icon: '✗' },
  { value: 'REDIRECTED', label: 'Redirige ailleurs', icon: '→' },
  { value: 'NO_SHOW', label: 'Patient non presente', icon: '?' },
] as const

export default function ConfirmationCard() {
  const { confirmResult, result } = useOrientationStore()
  const [feedbackType, setFeedbackType] = useState('')
  const [rating, setRating] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [feedbackSent, setFeedbackSent] = useState(false)
  const [sending, setSending] = useState(false)

  if (!confirmResult) return null

  const handleFeedback = async () => {
    if (!feedbackType) return
    setSending(true)
    try {
      await orientationAPI.submitFeedback(confirmResult.orientationId, {
        feedbackType,
        rating,
        comment,
        actualOutcome: feedbackType,
      })
      setFeedbackSent(true)
      toast.success('Merci pour votre retour')
    } catch (err: any) {
      toast.error(err?.message || 'Erreur lors de l\'envoi du feedback')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      {/* Success header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-neutral-900">Orientation confirmee</h3>
        <p className="text-sm text-neutral-500 mt-1">Le patient peut etre transfere</p>
      </div>

      {/* Details card */}
      <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4">
        <div className="flex justify-between items-center pb-3 border-b border-neutral-100">
          <span className="text-sm text-neutral-500">Reference</span>
          <span className="font-mono text-sm font-bold text-primary-700">{confirmResult.orientationRef}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-sm text-neutral-500">Structure choisie</span>
          <span className="text-sm font-medium text-neutral-900">{confirmResult.chosenStructureName}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-sm text-neutral-500">Statut</span>
          <span className="px-2.5 py-0.5 bg-success-100 text-success-800 rounded-full text-xs font-medium">
            {confirmResult.status}
          </span>
        </div>

        {confirmResult.serviceRequestId && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-neutral-500">ServiceRequest FHIR</span>
            <span className="font-mono text-xs text-neutral-600">{confirmResult.serviceRequestId.slice(0, 8)}...</span>
          </div>
        )}

        {result?.profileName && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-neutral-500">Profil de scoring</span>
            <span className="text-sm text-neutral-700">{result.profileName}</span>
          </div>
        )}
      </div>

      {/* Feedback form */}
      {!feedbackSent ? (
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-5 space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-amber-900">Retour post-orientation</h4>
            <p className="text-xs text-amber-600 mt-0.5">Aidez-nous a ameliorer les recommandations futures</p>
          </div>

          {/* Outcome type */}
          <div className="grid grid-cols-2 gap-2">
            {FEEDBACK_TYPES.map((ft) => (
              <button
                key={ft.value}
                type="button"
                onClick={() => setFeedbackType(ft.value)}
                className={`px-3 py-2 rounded-lg text-xs font-medium border-2 transition-colors text-left ${
                  feedbackType === ft.value
                    ? 'bg-amber-100 text-amber-800 border-amber-400'
                    : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <span className="mr-1.5">{ft.icon}</span>{ft.label}
              </button>
            ))}
          </div>

          {/* Star rating */}
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1.5">Pertinence de la recommandation</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className={`w-8 h-8 rounded text-lg transition-colors ${
                    rating !== null && star <= rating
                      ? 'text-amber-500'
                      : 'text-neutral-300 hover:text-amber-300'
                  }`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            placeholder="Commentaire optionnel..."
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />

          <button
            type="button"
            onClick={handleFeedback}
            disabled={!feedbackType || sending}
            className="w-full bg-amber-500 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? 'Envoi...' : 'Envoyer le retour'}
          </button>
        </div>
      ) : (
        <div className="bg-success-50 rounded-xl border border-success-200 p-4 text-center">
          <span className="text-success-700 text-sm font-medium">Merci — votre retour a ete enregistre</span>
        </div>
      )}

      {/* QR Code */}
      <div className="bg-neutral-50 rounded-xl p-6 text-center">
        <div className="bg-white rounded-lg p-3 inline-block mx-auto mb-3 shadow-sm">
          <QRCodeSVG
            value={`${window.location.origin}/dossier/${confirmResult.orientationRef}`}
            size={148}
            level="M"
            marginSize={0}
            fgColor="#1e293b"
          />
        </div>
        <p className="text-xs text-neutral-500">Scannez ce code pour acceder au dossier d'orientation</p>
        <p className="font-mono text-sm font-bold text-neutral-700 mt-2">{confirmResult.orientationRef}</p>
      </div>
    </div>
  )
}
